import { useEffect, useState } from 'react'
import { createAdminSportsbook, LOGO_ACCEPT, LOGO_MAX_BYTES, uploadSportsbookLogo } from './api'

/**
 * "+ NEW" on Distribution › SPORTSBOOKS. The operator twin of NewSportDialog:
 * creates the catalog row (name + brand color + fill flag) through the
 * create_sportsbook RPC, then — if a tile was chosen — uploads it to Supabase
 * Storage and writes the CDN URL to logo_url. Footprint (states / regions),
 * carried sports, affiliate links, regulatory text and the general link are
 * set afterwards in the editors, exactly like a hand-written migration
 * (0061 Rainbet, 0065 Betsson / Hard Rock Bet) used to leave them.
 */

const HEX_RE = /^#[0-9a-f]{6}$/i

interface Props {
  onClose: () => void
  // Fired once the row exists (logo may or may not have uploaded) so the
  // parent can refetch and navigate to the new operator.
  onCreated: (created: { id: number, name: string }) => void
}

export default function NewSportsbookDialog({ onClose, onCreated }: Props) {
  const [name, setName] = useState('')
  const [brandColor, setBrandColor] = useState('')
  const [logoFill, setLogoFill] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set once the row is created — if the follow-up upload fails we keep the
  // dialog open with the error and let the user continue without a logo
  // rather than leaving them unsure whether the operator exists.
  const [created, setCreated] = useState<{ id: number, name: string } | null>(null)

  useEffect(() => {
    if (!logoFile) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(logoFile)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [logoFile])

  const colorTrimmed = brandColor.trim()
  const colorValid = colorTrimmed === '' || HEX_RE.test(colorTrimmed)
  const canSubmit = name.trim().length > 0 && colorValid && !busy

  function pickFile(f: File | null) {
    setError(null)
    if (f && f.size > LOGO_MAX_BYTES) {
      setError(`"${f.name}" is ${(f.size / 1024 / 1024).toFixed(2)} MB — the limit is 2 MB`)
      setLogoFile(null)
      return
    }
    setLogoFile(f)
  }

  async function submit() {
    if (!canSubmit)
      return
    setBusy(true)
    setError(null)
    try {
      let row = created
      if (!row) {
        const res = await createAdminSportsbook({
          name: name.trim(),
          brand_color: colorTrimmed || null,
          logo_fill: logoFill,
        })
        row = { id: res.id, name: res.name }
        setCreated(row)
      }
      if (logoFile) {
        await uploadSportsbookLogo(row.id, logoFile, logoFill)
      }
      onCreated(row)
    }
    catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <div
      className="ad-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy)
          onClose()
      }}
    >
      <div className="ad-modal" role="dialog" aria-modal="true" aria-label="Create new operator">
        <header className="ad-modal-head">
          <h2 className="b-h2">NEW OPERATOR</h2>
          <button type="button" className="b-btn-ghost" onClick={onClose} aria-label="Close" disabled={busy}>✕</button>
        </header>

        {error && (
          <p className="ad-msg b-mono is-err">
            {created ? `${created.name} was created, but: ${error}` : error}
          </p>
        )}

        <label className="ad-field">
          <span className="b-mono-dim b-tiny">NAME — exactly as the book brands itself</span>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            className="ad-input"
            placeholder="e.g. Hard Rock Bet"
            autoFocus
            spellCheck={false}
            disabled={!!created}
            onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
          />
        </label>

        <label className="ad-field">
          <span className="b-mono-dim b-tiny">BRAND COLOR — #rrggbb, tile background</span>
          <span className="ad-color-row">
            <input
              type="color"
              className="ad-color-pick"
              value={HEX_RE.test(colorTrimmed) ? colorTrimmed : '#0db5ff'}
              onChange={e => setBrandColor(e.target.value)}
              aria-label="Brand color picker"
              disabled={!!created}
            />
            <input
              value={brandColor}
              onChange={e => setBrandColor(e.target.value)}
              className="ad-input"
              placeholder="optional"
              spellCheck={false}
              disabled={!!created}
              onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
            />
          </span>
          {!colorValid && <span className="b-mono-dim b-tiny">must be a #rrggbb hex</span>}
        </label>

        <div className="ad-field">
          <span className="b-mono-dim b-tiny">LOGO TILE — png / jpeg / webp / svg / gif, ≤ 2 MB</span>
          <div className="ad-file-row">
            <span
              className={`ad-tile-preview${logoFill ? ' is-fill' : ''}`}
              style={{ background: HEX_RE.test(colorTrimmed) ? colorTrimmed : 'transparent' }}
              aria-hidden="true"
            >
              {preview
                ? <img src={preview} alt="" />
                : <span className="ad-tile-preview-text">{(name.trim() || '??').slice(0, 2).toUpperCase()}</span>}
            </span>
            <label className="b-btn b-btn-ghost" style={{ cursor: busy ? 'default' : 'pointer' }}>
              {logoFile ? 'CHANGE FILE…' : 'CHOOSE FILE…'}
              <input
                type="file"
                className="ad-file-input"
                accept={LOGO_ACCEPT}
                disabled={busy}
                onChange={e => pickFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {logoFile && (
              <>
                <span className="ad-file-name">{logoFile.name}</span>
                <button type="button" className="b-btn-ghost b-tiny" onClick={() => pickFile(null)} disabled={busy}>REMOVE</button>
              </>
            )}
          </div>
        </div>

        <label className="ad-field ad-field-checkbox">
          <input
            type="checkbox"
            className="ad-checkbox"
            checked={logoFill}
            onChange={e => setLogoFill(e.target.checked)}
          />
          <span className="b-mono b-tiny">LOGO FILLS TILE — brand color is baked into the image (BetMGM / Rainbet style)</span>
        </label>

        <p className="b-mono-dim b-tiny ad-modal-hint">
          Approvals, regions, carried sports, affiliate URLs, regulatory text
          and the general link are set afterwards in the editors.
        </p>

        <div className="ad-modal-actions">
          <button type="button" className="b-btn-ghost" onClick={onClose} disabled={busy}>CANCEL</button>
          {created && error
            ? (
                <button type="button" className="b-btn b-btn-primary" onClick={() => onCreated(created)} disabled={busy}>
                  CONTINUE WITHOUT LOGO
                </button>
              )
            : null}
          <button type="button" className="b-btn b-btn-primary" onClick={submit} disabled={!canSubmit}>
            {busy ? (created ? 'UPLOADING…' : 'CREATING…') : created ? 'RETRY UPLOAD' : 'CREATE OPERATOR'}
          </button>
        </div>
      </div>
    </div>
  )
}
